import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Tpl3plService, Tpl3plConfig } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { InventoryService } from '../../../services/inventory.service';
import { OrdersService } from '../../../services/orders.service';

@Component({
	selector: 'app-tpl3pl-config-form',
	templateUrl: './tpl3pl-config-form.component.html',
	styleUrls: ['./tpl3pl-config-form.component.scss']
})
export class Tpl3plConfigFormComponent implements OnInit {
	configForm: UntypedFormGroup;
	config: Tpl3plConfig | null = null;
	loading = false;
	saving = false;
	testing = false;
	testResult: any = null;
	categories: any[] = [];
	productlines: any[] = [];
	brands: any[] = [];
	orderStatuses: any[] = [];

	constructor(
		private fb: UntypedFormBuilder,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private ordersService: OrdersService
	) {
		this.configForm = this.fb.group({
			enabled: [false],
			integration_type: ['API', Validators.required],
			warehouse_code: ['00', Validators.required],
			test_mode: [true],
			config_json: this.fb.group({
				api: this.fb.group({
					baseUrl: ['', [this.urlValidator()]],
					apiKey: [''],
					apiSecret: [''],
					timeout: [30, [Validators.min(1)]],
					retryAttempts: [3, [Validators.min(0)]]
				}),
				pgs: this.fb.group({
					baseUrl: ['', [this.urlValidator()]],
					tokenUrl: ['', [this.urlValidator()]],
					userId: [''],
					passwd: [''],
					defaultWarehouse: ['CM'],
					defaultOutboundChannel: ['B2C'],
					b2cServiceType: ['Ground'],
					inventoryMaxResults: [500, [Validators.min(1), Validators.max(2000)]],
					inventoryLoccode: [''],
					warehouseMapJson: [''],
					lob: this.fb.group({
						default: [''],
						byManufacturerIdJson: [''],
						byManufacturerNameJson: ['']
					})
				}),
				edi: this.fb.group({
					tradingPartnerId: [''],
					isaQualifier: ['ZZ'],
					gsQualifier: ['01']
				}),
				sftp: this.fb.group({
					host: [''],
					port: [22, [this.portValidator()]],
					username: [''],
					password: [''],
					remotePath: ['/inbound'],
					outboundPath: ['/outbound']
				}),
				mapping: this.fb.group({
					orderStatus: this.fb.group({}),
					carrierMapping: this.fb.group({}),
					b2bB2c: this.fb.group({})
				}),
				allowed_statuses: [[]],
				blocked_statuses: [[]],
				shipped_statuses: [[]],
				auto_invoice_on_shipment: [false],
				email_customer_on_3pl_invoice: [false],
				status_on_export_success: [''],
				status_on_shipment_received: [''],
				status_on_tracking_in_transit: [''],
				status_on_tracking_delivered: [''],
				status_on_invoice_failed: [''],
				status_on_shipment_approved: [''],
				sku_categories: [[]],
				sku_lines: [[]],
				sku_manufacturers: [[]],
				skip_gl_for_3pl_inventory: [false],
			})
		});
	}

	compareByManufacturerId = (a: any, b: any): boolean => Number(a) === Number(b);

	/** Sales order status IDs for API config; accepts saved arrays or legacy comma-separated strings. */
	private normalizeOrderStatusIds(value: unknown): number[] {
		if (Array.isArray(value)) {
			return value.map((x: any) => Number(x)).filter((n: number) => !isNaN(n));
		}
		if (typeof value === 'string') {
			return (value || '')
				.split(/[\s,]+/)
				.map((x: string) => parseInt(x.trim(), 10))
				.filter((n: number) => !isNaN(n));
		}
		return [];
	}

	get orderStatusesForSelect(): any[] {
		return [...this.orderStatuses]
			.filter((s) => s && !isNaN(Number(s.order_status_id)))
			.sort((a, b) => {
				const na = Number(a.order_status_id);
				const nb = Number(b.order_status_id);
				if (na !== nb) {
					return na - nb;
				}
				return String(a?.name ?? '').localeCompare(String(b?.name ?? ''));
			});
	}

	private normalizeToArray(value: any): any[] {
		if (Array.isArray(value)) {
			return value;
		}
		if (!value || typeof value !== 'object') {
			return [];
		}
		if (Array.isArray(value.rows)) {
			return value.rows;
		}
		if (Array.isArray(value.data)) {
			return value.data;
		}
		if (Array.isArray(value.results)) {
			return value.results;
		}
		if (Array.isArray(value.items)) {
			return value.items;
		}
		return [];
	}

	ngOnInit(): void {
		this.loadConfig();
		this.inventoryService.getItemCategories().subscribe((c: any) => { this.categories = this.normalizeToArray(c); });
		this.inventoryService.getItemProductlines().subscribe((p: any) => { this.productlines = this.normalizeToArray(p); });
		this.inventoryService.getBrands().subscribe((b: any) => { this.brands = this.normalizeToArray(b); });
		this.ordersService.getOrderStatuses().subscribe((r: any) => { this.orderStatuses = this.normalizeToArray(r); });
		this.configForm.get('integration_type')?.valueChanges.subscribe(() => {
			this.updateIntegrationValidators();
		});
		this.updateIntegrationValidators();
	}

	loadConfig(): void {
		this.loading = true;
		this.tpl3plService.getConfig().subscribe({
			next: (config) => {
				this.config = config;
				const cj = config.config_json || {};
				if (!cj.pgs) { cj.pgs = {}; }
				const pgsForForm = { ...(cj.pgs as object) } as Record<string, unknown>;
				const lobRaw = pgsForForm.lob as Record<string, unknown> | undefined;
				if (lobRaw && typeof lobRaw === 'object' && !Array.isArray(lobRaw)) {
					const by = lobRaw.byManufacturerName;
					const byId = lobRaw.byManufacturerId;
					pgsForForm.lob = {
						default: (lobRaw.default as string) || '',
						byManufacturerIdJson:
							byId && typeof byId === 'object' && !Array.isArray(byId) ? JSON.stringify(byId, null, 2) : '',
						byManufacturerNameJson:
							by && typeof by === 'object' && !Array.isArray(by) ? JSON.stringify(by, null, 2) : ''
					};
				} else {
					pgsForForm.lob = { default: '', byManufacturerIdJson: '', byManufacturerNameJson: '' };
				}
				const wmRaw = pgsForForm.warehouseMap;
				delete pgsForForm.warehouseMap;
				pgsForForm.warehouseMapJson =
					wmRaw && typeof wmRaw === 'object' && !Array.isArray(wmRaw)
						? JSON.stringify(wmRaw, null, 2)
						: '';
				if (pgsForForm.inventoryLoccode === undefined || pgsForForm.inventoryLoccode === null) {
					pgsForForm.inventoryLoccode = '';
				} else {
					pgsForForm.inventoryLoccode = String(pgsForForm.inventoryLoccode);
				}
				const toArray = (a: unknown): unknown[] =>
					Array.isArray(a) ? [...a] : (typeof a === 'string' ? a.split(/[\s,]+/).map(s => s.trim()).filter(Boolean) : []);
				const toNumArray = (a: unknown): number[] =>
					Array.isArray(a) ? a.map((x: any) => Number(x)).filter((n: number) => !isNaN(n)) : [];
				this.configForm.patchValue({
					enabled: config.enabled,
					integration_type: config.integration_type,
					warehouse_code: config.warehouse_code,
					test_mode: config.test_mode,
					config_json: {
						...cj,
						pgs: pgsForForm,
						allowed_statuses: this.normalizeOrderStatusIds(cj.allowed_statuses),
						blocked_statuses: this.normalizeOrderStatusIds(cj.blocked_statuses),
						shipped_statuses: this.normalizeOrderStatusIds(cj.shipped_statuses),
						auto_invoice_on_shipment: !!cj.auto_invoice_on_shipment,
						email_customer_on_3pl_invoice: !!cj.email_customer_on_3pl_invoice,
						status_on_export_success: cj.status_on_export_success != null && cj.status_on_export_success !== '' ? Number(cj.status_on_export_success) : '',
						status_on_shipment_received: cj.status_on_shipment_received != null && cj.status_on_shipment_received !== '' ? Number(cj.status_on_shipment_received) : '',
						status_on_tracking_in_transit: cj.status_on_tracking_in_transit != null && cj.status_on_tracking_in_transit !== '' ? Number(cj.status_on_tracking_in_transit) : '',
						status_on_tracking_delivered: cj.status_on_tracking_delivered != null && cj.status_on_tracking_delivered !== '' ? Number(cj.status_on_tracking_delivered) : '',
						status_on_invoice_failed: cj.status_on_invoice_failed != null && cj.status_on_invoice_failed !== '' ? Number(cj.status_on_invoice_failed) : '',
						status_on_shipment_approved: cj.status_on_shipment_approved != null && cj.status_on_shipment_approved !== '' ? Number(cj.status_on_shipment_approved) : '',
						sku_categories: toArray(cj.sku_categories),
						sku_lines: toArray(cj.sku_lines),
						sku_manufacturers: toNumArray(cj.sku_manufacturers),
						skip_gl_for_3pl_inventory: !!cj.skip_gl_for_3pl_inventory,
					}
				});
				this.loading = false;
			},
			error: (error) => {
				this.globalSearchService.showNotification('Error loading configuration', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	private updateIntegrationValidators(): void {
		const integrationType = this.integrationType;
		const apiBaseUrl = this.configForm.get('config_json.api.baseUrl');
		const pgsBaseUrl = this.configForm.get('config_json.pgs.baseUrl');
		const pgsTokenUrl = this.configForm.get('config_json.pgs.tokenUrl');
		const pgsUserId = this.configForm.get('config_json.pgs.userId');
		const pgsPasswd = this.configForm.get('config_json.pgs.passwd');
		const ediPartnerId = this.configForm.get('config_json.edi.tradingPartnerId');
		const sftpHost = this.configForm.get('config_json.sftp.host');
		const sftpUser = this.configForm.get('config_json.sftp.username');
		const sftpPass = this.configForm.get('config_json.sftp.password');

		apiBaseUrl?.clearValidators();
		pgsBaseUrl?.clearValidators();
		pgsTokenUrl?.clearValidators();
		pgsUserId?.clearValidators();
		pgsPasswd?.clearValidators();
		ediPartnerId?.clearValidators();
		sftpHost?.clearValidators();
		sftpUser?.clearValidators();
		sftpPass?.clearValidators();

		if (integrationType === 'API') {
			apiBaseUrl?.setValidators([Validators.required, this.urlValidator()]);
		}

		if (integrationType === 'PGS') {
			pgsBaseUrl?.setValidators([Validators.required, this.urlValidator()]);
			pgsTokenUrl?.setValidators([this.urlValidator()]);
			pgsUserId?.setValidators([Validators.required]);
			pgsPasswd?.setValidators([Validators.required]);
		}

		if (integrationType === 'EDI') {
			ediPartnerId?.setValidators([Validators.required]);
		}

		if (integrationType === 'SFTP' || integrationType === 'FTP') {
			sftpHost?.setValidators([Validators.required]);
			sftpUser?.setValidators([Validators.required]);
			sftpPass?.setValidators([Validators.required]);
		}

		apiBaseUrl?.updateValueAndValidity();
		pgsBaseUrl?.updateValueAndValidity();
		pgsTokenUrl?.updateValueAndValidity();
		pgsUserId?.updateValueAndValidity();
		pgsPasswd?.updateValueAndValidity();
		ediPartnerId?.updateValueAndValidity();
		sftpHost?.updateValueAndValidity();
		sftpUser?.updateValueAndValidity();
		sftpPass?.updateValueAndValidity();
	}

	private urlValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;
			if (!value) {
				return null;
			}
			try {
				const parsed = new URL(value);
				if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
					return { url: true };
				}
				return null;
			} catch {
				return { url: true };
			}
		};
	}

	private portValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;
			if (value === null || value === undefined || value === '') {
				return null;
			}
			const port = Number(value);
			if (!Number.isInteger(port) || port < 1 || port > 65535) {
				return { port: true };
			}
			return null;
		};
	}

	saveConfig(): void {
		if (this.configForm.invalid) {
			this.globalSearchService.showNotification('Please fill in all required fields', 'warning', 'bottom', 'right');
			return;
		}

		const formValue = this.configForm.value;
		if (this.config?.enabled && !formValue.enabled) {
			const confirmed = window.confirm('Disable 3PL integration? This will stop exports and imports.');
			if (!confirmed) {
				return;
			}
		}

		this.saving = true;
		const cj = { ...formValue.config_json };
		if (cj.allowed_statuses !== undefined) {
			cj.allowed_statuses = this.normalizeOrderStatusIds(cj.allowed_statuses);
		}
		if (cj.blocked_statuses !== undefined) {
			cj.blocked_statuses = this.normalizeOrderStatusIds(cj.blocked_statuses);
		}
		if (cj.shipped_statuses !== undefined) {
			cj.shipped_statuses = this.normalizeOrderStatusIds(cj.shipped_statuses);
		}
		const toStatusId = (v: any): number | null => { if (v === undefined || v === null || v === '') return null; const n = parseInt(String(v), 10); return isNaN(n) ? null : n; };
		if (cj.status_on_export_success !== undefined) { const v = toStatusId(cj.status_on_export_success); cj.status_on_export_success = v; }
		if (cj.status_on_shipment_received !== undefined) { const v = toStatusId(cj.status_on_shipment_received); cj.status_on_shipment_received = v; }
		if (cj.status_on_tracking_in_transit !== undefined) { const v = toStatusId(cj.status_on_tracking_in_transit); cj.status_on_tracking_in_transit = v; }
		if (cj.status_on_tracking_delivered !== undefined) { const v = toStatusId(cj.status_on_tracking_delivered); cj.status_on_tracking_delivered = v; }
		if (cj.status_on_invoice_failed !== undefined) { const v = toStatusId(cj.status_on_invoice_failed); cj.status_on_invoice_failed = v; }
		if (cj.status_on_shipment_approved !== undefined) { const v = toStatusId(cj.status_on_shipment_approved); cj.status_on_shipment_approved = v; }
		const parseCsvStrings = (s: string): string[] =>
			(s || '').split(/[\s,]+/).map((x: string) => x.trim()).filter((v: string) => v.length > 0);
		if (Array.isArray(cj.sku_categories)) { /* keep */ } else if (cj.sku_categories !== undefined) { cj.sku_categories = parseCsvStrings(String(cj.sku_categories)); }
		if (Array.isArray(cj.sku_lines)) { /* keep */ } else if (cj.sku_lines !== undefined) { cj.sku_lines = parseCsvStrings(String(cj.sku_lines)); }
				if (Array.isArray(cj.sku_manufacturers)) {
			cj.sku_manufacturers = cj.sku_manufacturers.map((x: any) => Number(x)).filter((n: number) => !isNaN(n));
		} else if (cj.sku_manufacturers !== undefined) {
			cj.sku_manufacturers = parseCsvStrings(String(cj.sku_manufacturers)).map((x: string) => parseInt(x, 10)).filter((n: number) => !isNaN(n));
		}
		if (cj.skip_gl_for_3pl_inventory !== undefined) {
			cj.skip_gl_for_3pl_inventory = !!cj.skip_gl_for_3pl_inventory;
		}
		if (cj.auto_invoice_on_shipment !== undefined) {
			cj.auto_invoice_on_shipment = !!cj.auto_invoice_on_shipment;
		}
		if (cj.email_customer_on_3pl_invoice !== undefined) {
			cj.email_customer_on_3pl_invoice = !!cj.email_customer_on_3pl_invoice;
		}
		if (cj.pgs && (cj.pgs as any).inventoryMaxResults !== undefined && (cj.pgs as any).inventoryMaxResults !== null && (cj.pgs as any).inventoryMaxResults !== '') {
			const n = parseInt(String((cj.pgs as any).inventoryMaxResults), 10);
			(cj.pgs as any).inventoryMaxResults = !isNaN(n) ? Math.min(2000, Math.max(1, n)) : 500;
		}
		if (cj.pgs) {
			const pgs = cj.pgs as Record<string, unknown>;
			const invLoc = String(pgs.inventoryLoccode ?? '').trim();
			if (invLoc) {
				pgs.inventoryLoccode = invLoc;
			} else {
				delete pgs.inventoryLoccode;
			}
			const wmStr = String(pgs.warehouseMapJson ?? '').trim();
			delete pgs.warehouseMapJson;
			if (!wmStr) {
				delete pgs.warehouseMap;
			} else {
				try {
					const parsed = JSON.parse(wmStr);
					if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
						this.globalSearchService.showNotification('PGS warehouse map must be a JSON object', 'warning', 'bottom', 'right');
						this.saving = false;
						return;
					}
					const out: Record<string, string> = {};
					for (const k of Object.keys(parsed)) {
						const v = String((parsed as Record<string, unknown>)[k] ?? '').trim();
						if (v !== '') {
							out[String(k).trim()] = v;
						}
					}
					if (Object.keys(out).length > 0) {
						pgs.warehouseMap = out;
					} else {
						delete pgs.warehouseMap;
					}
				} catch {
					this.globalSearchService.showNotification('Invalid JSON in PGS warehouse map', 'warning', 'bottom', 'right');
					this.saving = false;
					return;
				}
			}
		}
		if (cj.pgs && (cj.pgs as any).lob) {
			const lobForm = (cj.pgs as any).lob;
			const lobDefault = String(lobForm.default ?? '').trim();
			const parseLobObjectJson = (raw: string, label: string): Record<string, string> | null => {
				const s = String(raw ?? '').trim();
				if (!s) {
					return {};
				}
				try {
					const parsed = JSON.parse(s);
					if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
						const out: Record<string, string> = {};
						for (const k of Object.keys(parsed)) {
							out[String(k)] = String((parsed as Record<string, unknown>)[k] ?? '').trim();
						}
						return out;
					}
					this.globalSearchService.showNotification(`${label} must be a JSON object`, 'warning', 'bottom', 'right');
					return null;
				} catch {
					this.globalSearchService.showNotification(`Invalid JSON in ${label}`, 'warning', 'bottom', 'right');
					return null;
				}
			};
			const idMapParsed = parseLobObjectJson(String(lobForm.byManufacturerIdJson ?? ''), 'LOB manufacturer ID map');
			if (idMapParsed === null) {
				this.saving = false;
				return;
			}
			const nameMapParsed = parseLobObjectJson(String(lobForm.byManufacturerNameJson ?? ''), 'LOB manufacturer name map');
			if (nameMapParsed === null) {
				this.saving = false;
				return;
			}
			const idMap: Record<string, string> = {};
			for (const k of Object.keys(idMapParsed)) {
				if (idMapParsed[k] !== '') {
					idMap[k] = idMapParsed[k];
				}
			}
			const byMap: Record<string, string> = {};
			for (const k of Object.keys(nameMapParsed)) {
				if (nameMapParsed[k] !== '') {
					byMap[k] = nameMapParsed[k];
				}
			}
			if (lobDefault || Object.keys(byMap).length > 0 || Object.keys(idMap).length > 0) {
				(cj.pgs as any).lob = {};
				if (lobDefault) {
					(cj.pgs as any).lob.default = lobDefault;
				}
				if (Object.keys(idMap).length > 0) {
					(cj.pgs as any).lob.byManufacturerId = idMap;
				}
				if (Object.keys(byMap).length > 0) {
					(cj.pgs as any).lob.byManufacturerName = byMap;
				}
			} else {
				delete (cj.pgs as any).lob;
			}
		}
		delete (cj as any).syncSchedule;
		delete (cj as any).sync_schedule;
		const config: Tpl3plConfig = {
			integration_type: formValue.integration_type,
			enabled: formValue.enabled,
			test_mode: formValue.test_mode,
			warehouse_code: formValue.warehouse_code,
			config_json: cj
		};

		this.tpl3plService.saveConfig(config).subscribe({
			next: (result: any) => {
				this.saving = false;
				if (result && result.success !== false) {
					this.globalSearchService.showNotification('Configuration saved successfully', 'success', 'bottom', 'right');
					this.loadConfig();
					this.tpl3plService.notifyConfigSaved();
				} else {
					const msg = (result && result.message) ? result.message : 'Save failed';
					this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				const msg = error?.error?.message || error?.message || 'Error saving configuration';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				this.saving = false;
			}
		});
	}

	testConnection(): void {
		this.testing = true;
		this.testResult = null;

		const formValue = this.configForm.value;
		const testConfig: Tpl3plConfig = {
			integration_type: formValue.integration_type,
			enabled: formValue.enabled,
			test_mode: formValue.test_mode,
			warehouse_code: formValue.warehouse_code,
			config_json: formValue.config_json
		};

		this.tpl3plService.testConnection(testConfig).subscribe({
			next: (result) => {
				this.testResult = result;
				this.testing = false;
				if (result.success) {
					this.globalSearchService.showNotification('Connection test successful', 'success', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification('Connection test failed: ' + result.message, 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.testResult = { success: false, message: 'Connection test error' };
				this.testing = false;
				this.globalSearchService.showNotification('Connection test error', 'danger', 'bottom', 'right');
			}
		});
	}

	get integrationType(): string {
		return this.configForm.get('integration_type')?.value || 'API';
	}

	get apiForm(): UntypedFormGroup {
		return this.configForm.get('config_json.api') as UntypedFormGroup;
	}

	get ediForm(): UntypedFormGroup {
		return this.configForm.get('config_json.edi') as UntypedFormGroup;
	}

	get sftpForm(): UntypedFormGroup {
		return this.configForm.get('config_json.sftp') as UntypedFormGroup;
	}

	get pgsForm(): UntypedFormGroup {
		return this.configForm.get('config_json.pgs') as UntypedFormGroup;
	}
}
